import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GridContext } from 'contexts/GridContext';
import { DropsContext } from 'contexts/DropsContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { CustomPagination } from 'utils/functions';
import { dropBoolean } from 'utils/drops';
import { useModal, useDialog } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Header from 'components/Header';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import styles from './styles';

const Modal = ({ item, onSubmit }) => {
  const defaultValues = {
    documento_item_id: item?.id,
    componente_id: null,
    operacao: '',
    sequencia: item?.DocumentoItemEstruturas?.length + 1,
    quantidade: '',
    perda: '',
    volume: '',
    comprimento: '',
    largura: '',
    altura: '',
    retornavel: null,
    produtobase_id: null,
    observacao: null,
  };
  const { control, handleSubmit } = useForm({ defaultValues });
  const { postLoading } = useContext(DocumentosContext);
  const { drops } = useContext(DropsContext);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" component="div" align="center">
          Informações Gerais
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <VirtualDrop
          name="componente_id"
          control={control}
          label="Componente"
          options={drops?.Produto}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask name="operacao" control={control} label="Operação" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask name="sequencia" control={control} label="Sequência" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask name="quantidade" control={control} label="Quantidade" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="volume" control={control} label="Volume" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="comprimento" control={control} label="Comprimento" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="largura" control={control} label="Largura" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="altura" control={control} label="Altura" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputMask name="perda" control={control} label="% Perda" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Dropdown
          name="retornavel"
          control={control}
          label="Retornável"
          options={dropBoolean}
        />
      </Grid>
      <Grid item xs={12}>
        <VirtualDrop
          name="produtobase_id"
          control={control}
          label="Produto Base"
          options={drops?.Produto?.filter(
            (f) => f?.tipolinha === '01-Matéria prima'
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Input
          rows={3}
          multiline
          name="observacao"
          control={control}
          label="Observação"
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          color="primary"
          variant="contained"
          loading={postLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const Estruturas = () => {
  const { id } = useParams();
  const [itens, setItens] = useState([]);
  const { openModal, closeModal } = useModal();
  const { openDialog, closeDialog } = useDialog();
  const [selected, setSelected] = useState(null);
  const { drops } = useContext(DropsContext);
  const { deleteGrid } = useContext(GridContext);
  const { getLoading, getDocumentoItens, postLoading, postDocumento } =
    useContext(DocumentosContext);
  const selecionado = itens?.find((f) => f?.id === selected) || null;
  const estruturas = selecionado?.DocumentoItemEstruturas || [];

  const produtoMap = useMemo(() => {
    const map = new Map();
    drops?.Produto?.forEach((p) => map?.set(String(p?.value), p?.label));
    return map;
  }, [drops?.Produto]);

  const produtoOptions = useMemo(
    () => [
      { value: null, label: 'NENHUM' },
      ...(drops?.Produto?.map((p) => ({
        value: p?.value,
        label: p?.label,
      })) ?? []),
    ],
    [drops?.Produto]
  );

  const produtoBaseOptions = useMemo(
    () => [
      { value: null, label: 'NENHUM' },
      ...(drops?.Produto?.filter(
        (f) => f?.tipolinha === '01-Matéria prima'
      )?.map((p) => ({
        value: p?.value,
        label: p?.label,
      })) ?? []),
    ],
    [drops?.Produto]
  );

  const formatProduto = ({ value }) => {
    if (value == null) return '';
    return produtoMap.get(String(value)) ?? String(value);
  };

  const fetchData = () => {
    getDocumentoItens({ id, cb: setItens });
    closeModal();
    closeDialog();
  };

  const onCellEditCommit = ({ id, field, value }) => {
    setItens((prev) =>
      prev.map((item) => {
        if (item?.id !== selected) return item;

        return {
          ...item,
          edit: true,
          DocumentoItemEstruturas: item?.DocumentoItemEstruturas.map(
            (estrutura) =>
              estrutura?.id === id
                ? { ...estrutura, [field]: value }
                : estrutura
          ),
        };
      })
    );
  };

  const handleChange = (id) => setSelected(id);

  const handleSubmit = () => {
    const data = {
      Itens: itens
        ?.filter((f) => Boolean(f?.edit))
        ?.map((i) => ({
          id: i?.id,
          documento_id: i?.documento_id,
          Estruturas: i?.DocumentoItemEstruturas,
        })),
    };

    postDocumento({ data, cb: fetchData });
  };

  const handleDelete = (id) => {
    const block = itens?.some((s) => Boolean(s?.edit));
    if (block) {
      return alert('Antes de prosseguir, é necessário salvar as alterações');
    }

    const params = { rotina: 'DocumentoItemEstrutura', id };

    openModal(
      <DeleteModal onSubmit={() => deleteGrid({ params, cb: fetchData })} />
    );
  };

  const handleCreate = (estrutura) => {
    const data = {
      Itens: [
        {
          id: selecionado?.id,
          documento_id: selecionado?.documento_id,
          Estruturas: [estrutura],
        },
      ],
    };

    postDocumento({ data, cb: fetchData });
  };

  const handleAdd = () => {
    const block = itens?.some((s) => Boolean(s?.edit));
    if (block) {
      return alert('Antes de prosseguir, é necessário salvar as alterações');
    }

    openDialog(
      <Modal item={selecionado} onSubmit={handleCreate} />,
      'Adicionar Estrutura'
    );
  };

  useEffect(fetchData, []);

  useEffect(() => {
    if (!selected && itens?.length) {
      setSelected(itens?.[0]?.id);
    }
  }, [itens]);

  return (
    <Container>
      <Header titulo="Configurações da Estrutura" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4} display="flex">
          <Card title="Itens" style={styles?.card}>
            <Box sx={styles?.cardContainer}>
              <List>
                {itens?.map((i, index) => (
                  <ListItemButton
                    divider={index + 1 < itens?.length}
                    key={i?.id}
                    selected={selected === i?.id}
                    onClick={() => handleChange(i?.id)}
                  >
                    <ListItemText
                      primary={i?.descricao}
                      secondary={
                        Boolean(i?.referencia) && `Referência: ${i?.referencia}`
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={8} display="flex">
          <Card title="Estrutura" style={styles?.card}>
            <Grid container spacing={2}>
              <Grid item xs={12} textAlign="center">
                <Button color="primary" variant="outlined" onClick={handleAdd}>
                  Adicionar
                </Button>
              </Grid>
              <Grid item xs={12}>
                <DataGrid
                  rows={estruturas}
                  columns={[
                    {
                      field: 'actions',
                      headerName: 'Ações',
                      type: 'actions',
                      flex: 0,
                      getActions: ({ id }) => [
                        <GridActionsCellItem
                          icon={<Icon>delete</Icon>}
                          label="Excluir"
                          onClick={() => handleDelete(id)}
                        />,
                      ],
                    },
                    {
                      field: 'sequencia',
                      headerName: 'Sequência',
                      type: 'number',
                      width: 120,
                      sortable: false,
                      editable: true,
                    },
                    {
                      field: 'componente_id',
                      headerName: 'Componente',
                      type: 'singleSelect',
                      editable: true,
                      flex: 1,
                      minWidth: 300,
                      sortable: false,
                      valueOptions: produtoOptions,
                      valueFormatter: formatProduto,
                    },
                    {
                      field: 'sigla',
                      headerName: 'Unidade de Medida',
                      width: 150,
                      sortable: false,
                      editable: false,
                      valueGetter: ({ row }) =>
                        row?.Produto?.UnidadeMedida?.sigla || '',
                    },
                    {
                      field: 'quantidade',
                      headerName: 'Quantidade',
                      type: 'number',
                      width: 120,
                      sortable: false,
                      editable: true,
                    },
                    {
                      field: 'comprimento',
                      headerName: 'Comprimento',
                      type: 'number',
                      width: 120,
                      sortable: false,
                      editable: true,
                    },
                    {
                      field: 'largura',
                      headerName: 'Largura',
                      type: 'number',
                      width: 120,
                      sortable: false,
                      editable: true,
                    },
                    {
                      field: 'altura',
                      headerName: 'Altura',
                      type: 'number',
                      width: 120,
                      sortable: false,
                      editable: true,
                    },
                    {
                      field: 'medida',
                      headerName: 'Medida',
                      type: 'number',
                      width: 120,
                      sortable: false,
                      editable: false,
                      valueGetter: ({ row }) => {
                        const largura = Number(row?.largura) || 0;
                        const comprimento = Number(row?.comprimento) || 0;
                        return largura * comprimento;
                      },
                    },
                    {
                      field: 'produtobase_id',
                      headerName: 'Produto Base',
                      type: 'singleSelect',
                      editable: true,
                      flex: 1,
                      minWidth: 300,
                      sortable: false,
                      valueOptions: produtoBaseOptions,
                      valueFormatter: formatProduto,
                    },
                    {
                      field: 'observacao',
                      headerName: 'Observação',
                      width: 300,
                      sortable: false,
                      editable: true,
                    },
                  ]}
                  autoHeight
                  pagination
                  pageSize={10}
                  density="compact"
                  disableColumnMenu
                  showCellRightBorder
                  loading={postLoading}
                  showColumnRightBorder
                  disableSelectionOnClick
                  localeText={localeText}
                  onCellEditCommit={onCellEditCommit}
                  components={{
                    Pagination: CustomPagination,
                    LoadingOverlay: LinearProgress,
                  }}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            variant="contained"
            loading={postLoading}
            onClick={handleSubmit}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
      <Fade in={getLoading}>
        <Backdrop open={getLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default Estruturas;
